import {
  getV1CompaniesCompanyIdOrdersDispatch,
  GetV1CompaniesCompanyIdOrdersDispatchData,
  Job_Read,
  Order_Read,
} from '@treadinc/horizon-api-spec';
import axios from 'axios';
import dayjs from 'dayjs';
import { t } from 'i18next';

import useCancelToken from '~hooks/useCancelToken/useCancelToken';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { Filters } from '~store/LiveMapStoreNew';

import { Job } from '../useJob/models';
import { Order } from '../useOrders/models';

export const useLiveMapNew = () => {
  const { liveMapStoreNew } = useStores();
  const getCompanyOrdersByCompanyIdCancelToken = useCancelToken();

  // Probe = the exact query the sidebar would issue right after clearing filters
  // (search + retained jobStates/orderStates identity, no dates/user filters),
  // capped at one row. Powers the "Clear filters and search again" hint.
  // Failures stay silent: the hint simply doesn't show. Results
  // are dropped if liveMapStoreNew.filters was swapped mid-flight (resetOrders
  // replaces the object, making reference identity a request generation).
  const checkUnfilteredResultsCancelToken = useCancelToken();
  const checkUnfilteredResults = async (companyId: string, filtersAtLaunch: Filters) => {
    const cancelToken = checkUnfilteredResultsCancelToken.issueToken();

    try {
      const query: GetV1CompaniesCompanyIdOrdersDispatchData['query'] = {
        'page[limit]': 1,
        'search[job][datagrid]': filtersAtLaunch.search?.trim() ?? '',
      };

      if (filtersAtLaunch.jobStates?.length) {
        query['filter[job][states]'] = filtersAtLaunch.jobStates;
      }

      if (filtersAtLaunch.orderStates?.length) {
        query['filter[states]'] = filtersAtLaunch.orderStates;
      }

      const response = await getV1CompaniesCompanyIdOrdersDispatch({
        path: { 'company-id': companyId },
        query,
        cancelToken,
      });

      if (liveMapStoreNew.filters === filtersAtLaunch) {
        liveMapStoreNew.setHasUnfilteredResults(response.data.data.length > 0);
      }
    } catch (error) {
      if (!axios.isCancel(error) && liveMapStoreNew.filters === filtersAtLaunch) {
        liveMapStoreNew.setHasUnfilteredResults(false);
      }
    } finally {
      checkUnfilteredResultsCancelToken.discardToken();
    }
  };

  const getCompanyOrdersByCompanyId = async ({ companyId }: { companyId: string }) => {
    liveMapStoreNew.ordersFetchStart();
    // Cancel any in-flight unfiltered-results check from a prior search so it
    // cannot overwrite hasUnfilteredResults after this fetch resets the state.
    checkUnfilteredResultsCancelToken.issueToken();
    checkUnfilteredResultsCancelToken.discardToken();

    const cancelToken = getCompanyOrdersByCompanyIdCancelToken.issueToken();

    const { pagination, filters } = liveMapStoreNew;

    const query: GetV1CompaniesCompanyIdOrdersDispatchData['query'] = {
      'page[limit]': pagination.limit,
    };

    if (pagination.before) {
      query['page[before]'] = pagination.before;
    } else if (pagination.after) {
      query['page[after]'] = pagination.after;
    }

    if (filters?.search?.trim().length) {
      query['search[job][datagrid]'] = filters.search.trim();
    }

    if (filters?.customerAccountIds?.length) {
      query['filter[job][customer_account_ids]'] = filters.customerAccountIds;
    }

    if (filters?.dispatchNumberIds?.length) {
      query['filter[dispatch_number_ids]'] = filters.dispatchNumberIds;
    }

    if (filters?.driverIds?.length) {
      query['filter[job][driver_ids]'] = filters.driverIds;
    }

    if (filters?.projectsExternalIds?.length) {
      query['filter[job][external_ids]'] = filters.projectsExternalIds;
    }

    if (filters?.projectIds?.length) {
      query['filter[job][project_ids]'] = filters.projectIds;
    }

    if (filters?.vendorAccountIds?.length) {
      query['filter[job][vendor_account_ids]'] = filters.vendorAccountIds;
    }

    if (filters?.orderStates?.length) {
      query['filter[states]'] = filters.orderStates;
    }

    if (filters?.startDate?.length) {
      query['filter[job][start_date]'] = dayjs
        .tz(filters.startDate)
        .startOf('day')
        .toISOString();
    }

    if (filters?.endDate?.length) {
      query['filter[job][end_date]'] = dayjs
        .tz(filters.endDate)
        .endOf('day')
        .toISOString();
    }

    if (filters?.jobStates?.length) {
      query['filter[job][states]'] = filters.jobStates;
    }

    if (filters?.pickUpSites?.length) {
      query['filter[job][pickup_site_ids]'] = filters.pickUpSites;
    }

    if (filters?.dropoffSites?.length) {
      query['filter[job][dropoff_site_ids]'] = filters.dropoffSites;
    }

    if (filters?.requesterIds?.length) {
      query['filter[requester_ids]'] = filters.requesterIds;
    }

    if (filters?.siteTypes?.length) {
      query['filter[job][site_types]'] = filters.siteTypes;
    }

    if (filters?.departmentIds?.length) {
      query['filter[department_ids]'] = filters.departmentIds;
    }

    try {
      const response = await getV1CompaniesCompanyIdOrdersDispatch({
        path: { 'company-id': companyId },
        query,
        cancelToken,
      });
      const orders = response.data.data.map((order) => Order.parse(order));
      const pagination = extractPagination(response);
      liveMapStoreNew.ordersFetchEnd(orders, pagination);

      const isPaginationFetch = Boolean(query['page[after]'] || query['page[before]']);
      const searchTerm = filters?.search?.trim();
      const shouldProbeForUnfilteredResults =
        !isPaginationFetch &&
        orders.length === 0 &&
        Boolean(searchTerm?.length) &&
        liveMapStoreNew.hasActiveNonSearchFilters;

      if (shouldProbeForUnfilteredResults) {
        void checkUnfilteredResults(companyId, filters);
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        liveMapStoreNew.ordersFetchEnd([]);
        liveMapStoreNew.setHasUnfilteredResults(false);
        connection.handleRequestError(error, t('error_messages.orders.failed_to_fetch'));
      }
    } finally {
      getCompanyOrdersByCompanyIdCancelToken.discardToken();
    }
  };

  const subscribeToJobsRTU = (companyId: string) => {
    const cable = connection.realTimeConnection;
    const channel = realTimeChannels.CompanyJobUpdateChannel;

    let subscription = cable?.subscriptions.subscriptions.find((sub) => {
      return sub.identifier.includes(channel);
    });

    if (!subscription) {
      subscription = cable?.subscriptions?.create?.(
        { channel, company_id: companyId },
        {
          connected() {},
          disconnected() {},
          received: ({ data }: { data: Job_Read }) => {
            const job = Job.parse(data);

            liveMapStoreNew.upsertJob(job);
          },
        },
      );
    }

    return subscription;
  };

  const subscribeToOrdersRTU = (companyId: string) => {
    const cable = connection.realTimeConnection;
    const channel = realTimeChannels.CompanyOrderUpdateChannel;

    let subscription = cable?.subscriptions.subscriptions.find((sub) => {
      return sub.identifier.includes(channel);
    });

    if (!subscription) {
      subscription = cable?.subscriptions?.create?.(
        { channel, company_id: companyId },
        {
          connected() {},
          disconnected() {},
          received: ({ data }: { data: Order_Read }) => {
            const order = Order.parse(data);

            liveMapStoreNew.orderUpdateEnd(order);
          },
        },
      );
    }

    return subscription;
  };

  return {
    getCompanyOrdersByCompanyId,
    subscribeToJobsRTU,
    subscribeToOrdersRTU,
  };
};
